import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import GaugeIcon from 'assets/svg/gauge.svg';
import ClockIcon from 'assets/svg/desktop_i_clock.svg';
import styles from './MaintenanceListTable.module.scss';
import DateFormat from 'common/components/dateFormat/dateFormat';
import { DataTableWithSidebar, DataTableColumn, OrderData } from 'common/components/dataTable';
import { VehicleMaintenanceDto } from 'api/vehicleMaintenances/models/VehicleMaintenanceDto';
import { VehicleMaintenanceStates } from 'api/vehicleMaintenances/enums/VehicleMaintenanceStates';
import RenderSVG from 'common/components/renderSVG/RenderSVG';
import Badge from 'common/components/badge/Badge';
import IconWithText from 'common/components/iconWithText/IconWithText';
import ReactTooltip from 'react-tooltip';
import { DATE_FORMAT_DEFAUT, DefaultAssetImage, LOGGER_LOG_TYPE, SEGMENT_FEATURES } from 'Config';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import { UserProfile } from 'api/account/models/UserProfile';
import { Reducers } from 'store/types';
import UsersService from 'api/users/UsersService';
import { useSelector } from 'react-redux';
import Dropdown from 'common/components/dropdown/Dropdown';
import DropdownItem from 'common/components/dropdown/DropdownItem';
import { FaEllipsisH, FaPlus, FaFileContract } from 'react-icons/fa';
import VehicleMaintenancesService from 'api/vehicleMaintenances/VehicleMaintenancesService';
import Logger from 'common/services/Logger';
import { useToasts } from 'react-toast-notifications';
import MoneyFormat from 'common/components/moneyFormat/MoneyFormat';
import DeletedVehicleCircle from 'common/components/deletedVehicleCircle/DeletedVehicleCircle';
import Loading from 'common/services/Loading';
import { VehicleMaintenancesSearchCriteria } from 'api/vehicleMaintenances/models/VehicleMaintenancesSearchCriteria';
import moment from 'moment';

// Extended type for table rows with all needed fields
type MaintenanceTableRow = VehicleMaintenanceDto & {
    typeName: string;
    maintenanceDescription: string;
    maintenanceState: string;
    schedulingDate: Date | undefined;
    schedulingEndDate: Date | undefined;
    kilometerDate: number | string | undefined;
    hoursValue: number | string | undefined;
    predictedMaintenanceDate: Date | undefined;
    performedMaintenanceDate: Date | undefined;
    costValue: number;
    supplierDisplay: string;
    vehicleRegistration: string;
    vehicleBrand: string;
    vehicleModel: string;
    vehicleImage: string;
    reminderPredictedDisplay: string;
    reminderSchedulingDisplay: string;
    vehicleSegmentDisplay: string;
    vehicleLocalDisplay: string;
};

export type Props = {
    criteria?: VehicleMaintenancesSearchCriteria;
    vehicleId?: string;
    vehicleSegmentFeatures: string[]
    editable: boolean;
    items: VehicleMaintenanceDto[];
    totalItems: number;
    isLoadingMore: boolean;
    hasMore: boolean;
    onTableFilter: (field: string, isFilterAsc: boolean) => void;
    onLoadMore: () => void;
    onClickItem: (item: VehicleMaintenanceDto, isDetails: boolean, event: any) => void;
    onDeleteFromList?: () => void;
    reRenderSearch: number;
    scrollToRowId?: string | null;
    // Bidirectional scroll support
    hasMorePrevious?: boolean;
    isLoadingPrevious?: boolean;
    onLoadPrevious?: () => void;
    onFetchAllForExport?: () => Promise<VehicleMaintenanceDto[]>;
    exportRef?: React.MutableRefObject<{ exportExcel: () => Promise<void> } | null>;
};

const MaintenanceListTable = ({
    reRenderSearch,
    criteria,
    vehicleId,
    vehicleSegmentFeatures,
    editable,
    items,
    totalItems,
    isLoadingMore,
    hasMore,
    onTableFilter,
    onLoadMore,
    onClickItem,
    onDeleteFromList,
    scrollToRowId,
    hasMorePrevious,
    isLoadingPrevious,
    onLoadPrevious,
    onFetchAllForExport,
    exportRef,
}: Props) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();

    const imageCacheKey = useRef(new Date().getTime());
    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasVehicleMaintenanceWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_VEHICLE_MAINTENANCES_WRITE']);
    const [itemToRemove, setItemToRemove] = useState<VehicleMaintenanceDto | null>(null);
    const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);

    const renderKilometersCell = (row: VehicleMaintenanceDto) => {
        return (
            <div className={styles.cellContainer}>
                {showField(SEGMENT_FEATURES.KM) && <div className={styles.firstRow}>
                    <IconWithText
                        icon={GaugeIcon}
                        value={row.state == VehicleMaintenanceStates.PERFORM ? row.kilometers : row.adviseMaintenanceKilometers}
                        suffix={t('common.kilometers_minified')}
                        isNumeric={true}
                    />
                </div>}
            </div>
        )
    };

    const renderHoursCell = (row: VehicleMaintenanceDto) => {
        return (
            <div className={styles.cellContainer}>
                {showField(SEGMENT_FEATURES.HOURS) && <div className={styles.firstRow}>
                    <IconWithText
                        icon={ClockIcon}
                        value={row.state == VehicleMaintenanceStates.PERFORM ? row.hours : row.adviseMaintenanceHours}
                        suffix={t('common.hours_symbol')}
                        isNumeric={true}
                    />
                </div>}
            </div>
        )
    };

    const renderPredictedMaintenanceDateCell = (row: VehicleMaintenanceDto) => {
        if (!!row.adviseMaintenanceDate) {
            return (
                <>
                    <DateFormat value={row.adviseMaintenanceDate} format={DATE_FORMAT_DEFAUT} />
                </>
            );
        }
        return <>-</>;
    };

    const renderPerformedMaintenanceDateCell = (row: VehicleMaintenanceDto) => {
        if (!!row.maintenanceDate) {
            return (
                <>
                    <DateFormat value={row.maintenanceDate} format={DATE_FORMAT_DEFAUT} />{row.maintenanceHour && <> | {row.maintenanceHour}</>}
                </>
            );
        }
        return <>-</>;
    };

    const renderPaymentDateCell = (row: VehicleMaintenanceDto) => {
        if (row.paymentDate && !row.maintenanceContractsId) {
            return (
                <>
                    <DateFormat value={row.paymentDate} format={DATE_FORMAT_DEFAUT} />
                </>
            );
        }
        return <>-</>;
    };

    const renderTableActionCell = (
        row: VehicleMaintenanceDto,
    ) => {
        const urlDetails = vehicleId ? `/vehicles/details/${vehicleId}/maintenance/details/${row.id}` : `maintenances/details/${row.id}`;
        const urlEdit = vehicleId ? `/vehicles/details/${vehicleId}/maintenance/edit/${row.id}` : `maintenances/edit/${row.id}`;

        return (
            <Dropdown
                options={
                    <>
                        <DropdownItem url={urlDetails}>
                            {t('common.details')}
                        </DropdownItem>
                        { hasVehicleMaintenanceWritePolicy && editable && <DropdownItem url={urlEdit}>
                            {t('common.edit')}
                        </DropdownItem>}
                        { hasVehicleMaintenanceWritePolicy && editable && <DropdownItem onClick={() => showRemoveItemDialog(row)}>
                            {t('common.remove')}
                        </DropdownItem>}
                    </>
                }
            >
                <div>
                    <FaEllipsisH />
                </div>
            </Dropdown>
        );
    };

    const showRemoveItemDialog = (item: VehicleMaintenanceDto) => {
        setItemToRemove(item);
        setShowRemoveModal(true);
    };

    const onCancelRemove = () => {
        setItemToRemove(null);
        setShowRemoveModal(false);
    };

    const onRemove = async () => {
        if (itemToRemove === null) {
            addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
            return;
        }

        try {
            setShowRemoveModal(false);
            Loading.show();
            await VehicleMaintenancesService.remove(itemToRemove);
            onCancelRemove();
            addToast(t('common.messages.record_delete_success'), {
                appearance: 'success',
            });
            Loading.hide();
            if (onDeleteFromList) {
                onDeleteFromList();
            }

        }
        catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't delete maintenance`, error);
            addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
            Loading.hide();
        }
    };


    const showField = (feature: string) => {
        if (vehicleSegmentFeatures.length === 0) return true;
        return (vehicleSegmentFeatures.findIndex(x => x == feature) ?? -1) >= 0;
    }

    const renderComponentsCell = (row: VehicleMaintenanceDto) => {
        if (!row.components || row.components.length === 0) {
            return <span>-</span>;
        }

        const first = row.components[0];
        const remaining = row.components.slice(1);
        const tooltipId = `components-${row.id}`;
        const firstTooltipId = `component-first-${row.id}`;

        return (
            <div className={styles.componentsCell}>
                <span
                    className={styles.componentBadge}
                    data-tip={first.name}
                    data-for={firstTooltipId}
                >
                    {first.name}
                </span>
                <ReactTooltip id={firstTooltipId} effect="solid" place="top" />
                {remaining.length > 0 && (
                    <>
                        <span className={styles.componentCount} data-tip data-for={tooltipId}>
                            <FaPlus size={7} /> {remaining.length}
                        </span>
                        <ReactTooltip id={tooltipId} effect="solid" place="top">
                            <div>
                                <div style={{ marginBottom: '4px', fontSize: '11px', opacity: 0.8 }}>{t('maintenances.other_components')}:</div>
                                {remaining.map((comp, idx) => (
                                    <div key={idx}>{comp.name}</div>
                                ))}
                            </div>
                        </ReactTooltip>
                    </>
                )}
            </div>
        );
    };

    const tableColumns: DataTableColumn<MaintenanceTableRow>[] = useMemo(() => {
        const euroHeaderNote = `${t('common.unit')}: ${t('common.euro')}`;
        const kmHeaderNote = `${t('common.unit')}: ${t('common.kilometers_minified')}`;
        const hoursHeaderNote = `${t('common.unit')}: ${t('common.hours_symbol')}`;
        const cols: DataTableColumn<MaintenanceTableRow>[] = [
            {
                field: 'typeName',
                name: t('common.type'),
                orderColumn: 'mct.name',
                width: 130,
                exportValue: row => row.typeName ? (!row.typeIsSystem ? row.typeName : t(('common.maintenance_contract_type.' + row.typeName) as any) as string) : '-',
                renderCell: row =>
                    <div className={`${styles.rowFlex}`}>
                        {row.typeIconSvg && <RenderSVG item={row.typeIconSvg} className={styles.divSVG} />}
                        <span>{row.typeName ? (!row.typeIsSystem ? row.typeName : t(('common.maintenance_contract_type.' + row.typeName) as any)) : '-'}</span>
                    </div>,
            },
            {
                field: 'maintenanceDescription',
                name: t('common.description'),
                orderColumn: 'vm.description',
                width: 170,
                renderCell: row => <span>{row.description || '-'}</span>,
            },
            {
                field: 'maintenanceState',
                name: t('common.state'),
                orderColumn: 'vm.state',
                width: 100,
                hideOn: ['sm'],
                exportValue: row => row.state ? t(('maintenances.states.' + row.state) as any) as string : '-',
                renderCell: row => {
                    return (
                        <div>
                            {row.state == VehicleMaintenanceStates.PREDICTED ? <Badge text={row.state ? (t(('maintenances.states.' + row.state) as any)) : '-'} preset="primaryDark" />
                                : row.state == VehicleMaintenanceStates.PERFORM ? <Badge text={row.state ? (t(('maintenances.states.' + row.state) as any)) : '-'} preset="success" />
                                    : <Badge text={row.state ? (t(('maintenances.states.' + row.state) as any)) : '-'} preset="inactive" />}
                        </div>
                    );
                },
            },
            {
                field: 'schedulingDate',
                name: t('maintenances.start_date_maintenance'),
                orderColumn: 'vm.expected_date',
                width: 145,
                hideOn: ['sm'],
                exportValue: row => row.expectedDate ? moment(row.expectedDate).format(DATE_FORMAT_DEFAUT) + (row.expectedHour ? ' | ' + row.expectedHour : '') : '-',
                renderCell: row => {
                    return (
                        <div
                            data-tip={row.responsibleName ? `${t('common.assigned_to')}: ${row.responsibleName}` : undefined}
                            data-for="stateColumnTooltip"
                        >
                            {row.expectedDate ? (
                                <><DateFormat value={row.expectedDate} format={DATE_FORMAT_DEFAUT} />{row.expectedHour && <> | {row.expectedHour}</>}</>
                            ) : '-'}
                        </div>
                    );
                },
            },
            {
                field: 'schedulingEndDate',
                name: t('maintenances.end_date_maintenance'),
                orderColumn: 'vm.expected_end_date',
                width: 145,
                hideOn: ['sm'],
                exportValue: row => row.expectedEndDate ? moment(row.expectedEndDate).format(DATE_FORMAT_DEFAUT) + (row.expectedEndHour ? ' | ' + row.expectedEndHour : '') : '-',
                renderCell: row => {
                    return (
                        <>
                            {row.expectedEndDate ? (
                                <><DateFormat value={row.expectedEndDate} format={DATE_FORMAT_DEFAUT} />{row.expectedEndHour && <> | {row.expectedEndHour}</>}</>
                            ) : '-'}
                        </>
                    );
                },
            },
            {
                field: 'components',
                name: t('maintenances.list.components'),
                hideOrderButton: true,
                width: 130,
                hideOn: ['sm', 'md'],
                exportValue: row => row.components && row.components.length > 0 ? row.components.map(c => c.name).join(', ') : '-',
                renderCell: renderComponentsCell,
            },
            {
                field: 'kilometerDate',
                name: t('maintenances.list.advise_kilometer_date'),
                orderColumn: 'vm.kilometers',
                cellAlignment: 'right',
                columnCellAlignment: 'right',
                width: 130,
                hideOn: ['sm', 'md', 'lg'],
                headerNote: kmHeaderNote,
                exportValue: row => {
                    if (!showField(SEGMENT_FEATURES.KM)) return '';
                    return (row.state == VehicleMaintenanceStates.PERFORM ? row.kilometers : row.adviseMaintenanceKilometers) ?? '';
                },
                renderCell: renderKilometersCell,
            },
            {
                field: 'hoursValue',
                name: t('maintenances.list.advise_hours'),
                orderColumn: 'vm.hours',
                cellAlignment: 'right',
                columnCellAlignment: 'right',
                width: 110,
                hideOn: ['sm', 'md', 'lg'],
                headerNote: hoursHeaderNote,
                exportValue: row => {
                    if (!showField(SEGMENT_FEATURES.HOURS)) return '';
                    return (row.state == VehicleMaintenanceStates.PERFORM ? row.hours : row.adviseMaintenanceHours) ?? '';
                },
                renderCell: renderHoursCell,
            },
            {
                field: 'predictedMaintenanceDate',
                name: t('maintenances.add_maintenance_predicted_date') as string,
                orderColumn: 'vm.advise_maintenance_date',
                width: 130,
                hideOn: ['sm', 'md', 'lg'],
                exportValue: row => row.adviseMaintenanceDate ? moment(row.adviseMaintenanceDate).format(DATE_FORMAT_DEFAUT) : '-',
                renderCell: renderPredictedMaintenanceDateCell,
            },
            {
                field: 'performedMaintenanceDate',
                name: t('maintenances.list.perform_date_tooltip') as string,
                orderColumn: 'vm.maintenance_date',
                width: 130,
                hideOn: ['sm', 'md', 'lg'],
                exportValue: row => row.maintenanceDate ? moment(row.maintenanceDate).format(DATE_FORMAT_DEFAUT) + (row.maintenanceHour ? ' | ' + row.maintenanceHour : '') : '-',
                renderCell: renderPerformedMaintenanceDateCell,
            },
            {
                field: 'paymentDate',
                name: t('maintenances.payment_date') as string,
                orderColumn: 'vm.payment_date',
                width: 130,
                hideOn: ['sm', 'md', 'lg'],
                exportValue: row => row.paymentDate && !row.maintenanceContractsId ? moment(row.paymentDate).format(DATE_FORMAT_DEFAUT) : '-',
                renderCell: renderPaymentDateCell,
            },
            {
                field: 'costValue',
                name: t('common.cost'),
                orderColumn: 'vm.cost',
                cellAlignment: 'right',
                columnCellAlignment: 'right',
                width: 100,
                hideOn: ['sm', 'md'],
                headerNote: euroHeaderNote,
                exportValue: row => row.maintenanceContractsId ? t('maintenances.included_maintenance_contract') as string : row.cost !== undefined && row.cost !== null ? row.cost : '-',
                renderCell: row =>
                    <div className={styles.costColumn} style={{ textAlign: 'right', fontWeight: 'bold' }}>
                        {row.maintenanceContractsId
                            ? <span data-tip={t('maintenances.included_maintenance_contract')} data-for="contractTooltip">
                                <FaFileContract size={16} style={{ color: '#17a2b8' }} />
                              </span>
                            : row.cost !== undefined && row.cost !== null ? <MoneyFormat value={row.cost} suffix={t('common.euro')} fixedDecimalScale={true} /> : '-'
                        }
                    </div>,
            },
            {
                field: 'reminderPredictedDisplay',
                name: <span title={t('maintenances.list.reminder_predicted_full') as string}>{t('maintenances.list.reminder_predicted')}</span>,
                orderColumn: 'reminder_predicted_count',
                width: 100,
                hideOn: ['sm', 'md', 'lg'],
                exportValue: row => (row.reminderPredictedCount ?? 0) > 0 ? t('common.yes') as string : t('common.no') as string,
                renderCell: row => {
                    const count = row.reminderPredictedCount ?? 0;
                    const tooltipId = `reminder-predicted-${row.id}`;
                    return (
                        <>
                            <span data-tip data-for={tooltipId}>
                                {count > 0 ? t('common.yes') : t('common.no')}
                            </span>
                            {count > 0 && (
                                <ReactTooltip id={tooltipId} effect="solid" place="top">
                                    {t('maintenances.list.reminder_count', { count })}
                                </ReactTooltip>
                            )}
                        </>
                    );
                },
            },
            {
                field: 'reminderSchedulingDisplay',
                name: t('maintenances.list.reminder_scheduling') as string,
                orderColumn: 'reminder_scheduling_count',
                width: 100,
                hideOn: ['sm', 'md', 'lg'],
                exportValue: row => (row.reminderSchedulingCount ?? 0) > 0 ? t('common.yes') as string : t('common.no') as string,
                renderCell: row => {
                    const count = row.reminderSchedulingCount ?? 0;
                    const tooltipId = `reminder-scheduling-${row.id}`;
                    return (
                        <>
                            <span data-tip data-for={tooltipId}>
                                {count > 0 ? t('common.yes') : t('common.no')}
                            </span>
                            {count > 0 && (
                                <ReactTooltip id={tooltipId} effect="solid" place="top">
                                    {t('maintenances.list.reminder_count', { count })}
                                </ReactTooltip>
                            )}
                        </>
                    );
                },
            },
            {
                field: 'supplierDisplay',
                name: t('common.supplier'),
                orderColumn: 'supplier.name',
                width: 130,
                hideOn: ['sm', 'md', 'lg'],
                exportValue: row => row.supplierName || '-',
                renderCell: row => <>
                    <div data-for="main2" data-tip={row.supplierUrl ? (row.supplierName || '-') : undefined}>
                        {row.supplierUrl && <img src={row.supplierUrl} style={{ height: '2rem' }}></img>}
                        {!row.supplierUrl && (row.supplierName || '-')}
                    </div>
                    <ReactTooltip id="main2" />
                </>,
            },
        ];

        if (!vehicleId) {
            cols.unshift(
                {
                    field: 'vehicleImage',
                    name: t('common.image'),
                    hideOrderButton: true,
                    width: 75,
                    hideOn: ['sm', 'md'],
                    excludeFromExport: true,
                    renderCell: row =>
                        <div className={styles.imageDescCell}>
                            <div className={styles.contentImage}>
                                <div className={styles.img} style={{ width: '5rem', height: '3rem', backgroundImage: row?.photoUrl ? 'url(' + row?.photoUrl + '?_=' + imageCacheKey.current + ')' : row?.vehicleSegmentDefaultPhotoUrl ? 'url(' + row?.vehicleSegmentDefaultPhotoUrl + '?_=' + imageCacheKey.current + ')' : 'url(' + DefaultAssetImage + ')' }} />
                            </div>
                        </div>,
                },
                {
                    field: 'vehicleRegistration',
                    name: t('vehicle.registration') as string,
                    orderColumn: 'v.registration_number',
                    width: 130,
                    renderCell: row =>
                        <div className={styles.descCell}>
                            <div className={styles.columnPlate}>
                                {row.registrationNumber}
                                {!row.vehicleActive && <DeletedVehicleCircle id={row.id ?? ''} />}
                            </div>
                        </div>,
                },
                {
                    field: 'vehicleBrand',
                    name: t('vehicle.brand') as string,
                    orderColumn: 'vehicle_brands.name',
                    width: 100,
                    hideOn: ['sm'],
                    renderCell: row =>
                        <div className={styles.columnBrand}>
                            {row.vehicleBrandName || '-'}
                        </div>,
                },
                {
                    field: 'vehicleModel',
                    name: t('vehicle.model') as string,
                    orderColumn: 'vehicle_models.name',
                    width: 100,
                    hideOn: ['sm'],
                    renderCell: row =>
                        <div className={styles.columnBrand}>
                            {row.vehicleModelName || '-'}
                        </div>,
                },
                {
                    field: 'vehicleSegmentDisplay',
                    name: t('vehicle.type') as string,
                    orderColumn: 'vehicle_segments.name',
                    width: 130,
                    hideOn: ['sm', 'md', 'lg'],
                    exportValue: row => row.vehicleSegmentName || '-',
                    renderCell: row => <>{row.vehicleSegmentName || '-'}</>,
                },
                {
                    field: 'vehicleLocalDisplay',
                    name: t('vehicle.local') as string,
                    hideOrderButton: true,
                    width: 130,
                    hideOn: ['sm', 'md', 'lg'],
                    exportValue: row => row.vehicleLocalNames || '-',
                    renderCell: row => <>{row.vehicleLocalNames || '-'}</>,
                }
            );
        }

        return cols;
    }, [vehicleSegmentFeatures]);

    const toTableRow = (item: VehicleMaintenanceDto): MaintenanceTableRow => ({
        ...item,
        typeName: item.typeName || '',
        maintenanceDescription: item.description || '',
        maintenanceState: item.state || '',
        schedulingDate: item.expectedDate,
        schedulingEndDate: item.expectedEndDate,
        kilometerDate: item.adviseMaintenanceKilometers || item.kilometers || '',
        hoursValue: item.adviseMaintenanceHours || item.hours || '',
        predictedMaintenanceDate: item.adviseMaintenanceDate,
        performedMaintenanceDate: item.maintenanceDate,
        paymentDate: item.paymentDate,
        costValue: item.cost || 0,
        supplierDisplay: item.supplierName || '',
        vehicleRegistration: item.registrationNumber || '',
        vehicleBrand: item.vehicleBrandName || '',
        vehicleModel: item.vehicleModelName || '',
        vehicleImage: item.photoUrl || '',
        reminderPredictedDisplay: (item.reminderPredictedCount ?? 0) > 0 ? 'Sim' : 'Não',
        reminderSchedulingDisplay: (item.reminderSchedulingCount ?? 0) > 0 ? 'Sim' : 'Não',
        vehicleSegmentDisplay: item.vehicleSegmentName || '',
        vehicleLocalDisplay: item.vehicleLocalNames || '',
    });

    // Convert rows to extended type for table
    const tableRows: MaintenanceTableRow[] = useMemo(() => {
        return items.map(toTableRow);
    }, [items]);

    const fetchAllForExcelExport = useCallback(async (): Promise<MaintenanceTableRow[]> => {
        if (!onFetchAllForExport) return [];
        const allMaintenances = await onFetchAllForExport();
        return allMaintenances.map(toTableRow);
    }, [onFetchAllForExport]);

    // Handle order change from DataTable
    const handleOrderChange = (orderData: OrderData<MaintenanceTableRow>) => {
        const orderColumn = orderData.orderColumn || (orderData.field as string);
        onTableFilter(orderColumn, orderData.isOrderAsc);
    };

    // Map orderColumn to field for proper sorting indicator
    const getFieldFromOrderColumn = (orderColumn: string | undefined): keyof MaintenanceTableRow => {
        const mapping: Record<string, keyof MaintenanceTableRow> = {
            'mct.name': 'typeName',
            'vm.description': 'maintenanceDescription',
            'vm.state': 'maintenanceState',
            'vm.expected_date': 'schedulingDate',
            'vm.expected_end_date': 'schedulingEndDate',
            'vm.kilometers': 'kilometerDate',
            'vm.hours': 'hoursValue',
            'vm.advise_maintenance_date': 'predictedMaintenanceDate',
            'vm.maintenance_date': 'performedMaintenanceDate',
            'vm.payment_date': 'paymentDate',
            'vm.cost': 'costValue',
            'supplier.name': 'supplierDisplay',
            'reminder_predicted_count': 'reminderPredictedDisplay',
            'reminder_scheduling_count': 'reminderSchedulingDisplay',
            'vehicle_segments.name': 'vehicleSegmentDisplay',
            'v.registration_number': 'vehicleRegistration',
            'vehicle_brands.name': 'vehicleBrand',
            'vehicle_models.name': 'vehicleModel',
        };
        return mapping[orderColumn ?? ''] ?? 'typeName';
    };

    // Get default order from criteria
    const defaultOrder: OrderData<MaintenanceTableRow> = useMemo(() => {
        const orderColumn = criteria?.orderColumn ?? (showField(SEGMENT_FEATURES.KM) ? 'vm.kilometers' : 'vm.maintenance_date');
        return {
            field: getFieldFromOrderColumn(orderColumn),
            orderColumn: orderColumn,
            isOrderAsc: criteria?.orderBy === 'asc',
        };
    }, [criteria?.orderColumn, criteria?.orderBy]);

    return (
        <div className={styles.tableContainer}>
            <DataTableWithSidebar
                key={`${reRenderSearch}`}
                cacheKey={`maintenance-list-v3-${vehicleId || 'all'}`}
                columns={tableColumns}
                rows={tableRows}
                defaultHiddenColumns={['vehicleSegmentDisplay', 'vehicleLocalDisplay']}
                defaultOrder={defaultOrder}
                onRowClick={(row, _, event) => onClickItem(row, true, event)}
                onOrderChange={handleOrderChange}
                onRenderAction={renderTableActionCell}
                onHref={row => vehicleId ? `/vehicles/details/${vehicleId}/maintenance/details/${row.id}` : `/maintenances/details/${row.id}`}
                onRowId={row => row.id ?? ''}
                scrollToRowId={scrollToRowId}
                infiniteScroll={{
                    isLoading: isLoadingMore,
                    hasMore: hasMore,
                    onLoadMore: onLoadMore,
                    hasMorePrevious: hasMorePrevious,
                    isLoadingPrevious: isLoadingPrevious,
                    onLoadPrevious: onLoadPrevious,
                }}
                exportExcel={onFetchAllForExport ? {
                    fetchAll: fetchAllForExcelExport,
                    fileName: t('maintenances.title') as string,
                } : undefined}
                exportRef={exportRef}
                hideExportInSidebar={!!exportRef}
            />
            <QuestionYesNo
                onNo={onCancelRemove}
                onYes={onRemove}
                isVisible={showRemoveModal}
                message={t('common.messages.remove_maintenance')} />
            <ReactTooltip id="maintenanceGeneralTooltip" />
            <ReactTooltip id="stateColumnTooltip" effect="solid" html={true} />
            <ReactTooltip id="contractTooltip" effect="solid" />
        </div>
    );
}

export default MaintenanceListTable;
